import { useMemo } from "react";

import QUERY_TAGS from "../search/queryTags";
import useQuery from "../search/useQuery";

import { fetchSupplierAccessories, fetchSupplierProducts } from "./detailService";
import { mapAccessorySections, mapProduct } from "./detailMappers";

export const useSupplierProducts = ({ supplierId, containerOptionId, enabled = true }) => {
  const active = Boolean(supplierId) && Boolean(containerOptionId) && enabled;

  const query = useQuery({
    key: active ? `supplier-products:${supplierId}:${containerOptionId}` : null,
    tag: QUERY_TAGS.SUPPLIER_PRODUCTS,
    enabled: active,
    fetcher: () => fetchSupplierProducts({ supplierId, containerOptionId }),
  });

  const products = useMemo(
    () => (query.currentData || []).map(mapProduct).filter(Boolean),
    [query.currentData]
  );

  return {
    products,
    isLoading: active && !query.isSuccess && !query.isError,
    isError: query.isError,
    refetch: query.refetch,
  };
};

export const useSupplierAccessories = ({ supplierId, enabled = true }) => {
  const active = Boolean(supplierId) && enabled;

  const query = useQuery({
    key: active ? `supplier-accessories:${supplierId}` : null,
    tag: QUERY_TAGS.SUPPLIER_ACCESSORIES,
    enabled: active,
    fetcher: () => fetchSupplierAccessories(supplierId),
  });

  const sections = useMemo(
    () =>
      mapAccessorySections(query.currentData).filter(
        (section) => section.items.length
      ),
    [query.currentData]
  );

  return {
    sections,
    isLoading: active && !query.isSuccess && !query.isError,
    isError: query.isError,
    refetch: query.refetch,
  };
};

export const useFillTypes = ({ containerOptions, supportedIds }) =>
  useMemo(() => {
    const options = containerOptions || [];
    const supported = supportedIds?.length ? new Set(supportedIds) : null;

    return options
      .filter((option) => (supported ? supported.has(option.id) : true))
      .map((option) => ({
        id: option.id,
        code: option.code,
        label: option.defaultTitle || option.code,
        variableVolume: Boolean(option.variableVolume),
        accessory: Boolean(option.accessory),
      }));
  }, [containerOptions, supportedIds]);
